import React, {
  memo, useMemo, useRef, useCallback,
} from 'react'
import Form, { FormConsumer } from '@active/react-ui/Form'
import FormGroup from '@active/react-ui/FormGroup'
import FormLabel from '@active/react-ui/FormLabel'
import FormField from '@active/react-ui/FormField'
import Modal from '@active/react-ui/Modal'
import ModalBody from '@active/react-ui/ModalBody'
import ModalFooter from '@active/react-ui/ModalFooter'
import Grid from '@active/react-ui/Grid'
import GridItem from '@active/react-ui/GridItem'
import Input from '@active/react-ui/Input'
import Button from '@active/react-ui/Button'
import LinkButton from '@active/react-ui/LinkButton'
import {
  func, string, number,
} from 'prop-types'
import { Locale, withI18n } from '@active/react-utils/i18n'
import isEmpty from 'lodash/isEmpty'
import { maskSenderPhone } from '../../../../shared/utils/commonUtils'
import I18nMessage from '../../I18nMessage'
import './VerifyPhoneStepTwo.scss'

function VerifyPhoneStepTwo({
  title,
  phoneNumber,
  code,
  countDownNumber,
  handleClose,
  handleReject,
  handleConfirm,
  handleResend,
  okText,
  i18n,
  ...rest
},
) {
  const cancelText = 'passport.common.btn.back'
  const codeInputLabel = 'passport.verification.modal.input.placeholder'
  const codeInputPlaceHolder = 'passport.verification.modal.input.code.placeholder'
  const resendText = 'passport.lib.components.common.link.resend.code'
  const desc = useMemo(() => (
    <I18nMessage id="passport.phoneAuthentication.modal.description.codeSent" params={{ 0: maskSenderPhone(phoneNumber) }} />
  ), [phoneNumber])

  const countDownTips = useMemo(() => <I18nMessage className="countDown-tip" id="passport.verification.modal.countDown.tips" params={{ 0: `<span>${countDownNumber}</span>` }} />, [countDownNumber])

  const formRef = useRef()

  const handleSubmit = useCallback(async () => {
    const result = await formRef.current.validateForm({ shouldTouchFields: true })
    if (isEmpty(result)) {
      handleConfirm({
        ...formRef.current.values,
      })
    }
  }, [handleConfirm, formRef])

  return (
    <Modal
      title={i18n(title)}
      onClose={handleClose}
      size="md"
      shown
      {...rest}
      className="phone-verify-step-two"
    >
      <ModalBody className="modal-body phone-verify-step-two-content">
        <div>
          {desc}
        </div>
        <Form
          initialValues={{
            code,
          }}
        >
          <Grid fluid rowGap={0} colGap={0}>
            <GridItem span={12} spanMd={8}>
              <FormGroup name="code" withoutLayout required>
                <FormLabel verticalStart><Locale id={codeInputLabel} /></FormLabel>
                <FormField as={Input} placeholder={i18n(codeInputPlaceHolder)} />
              </FormGroup>
            </GridItem>
            <GridItem span={12} spanMd={8}>
              <div className="phone-verify-step-two-code-extra">
                <LinkButton disabled={countDownNumber > 0} onClick={handleResend}>
                  <Locale id={resendText} />
                </LinkButton>
                { countDownNumber > 0 && (
                <div>
                  { countDownTips }
                </div>
                ) }
              </div>
            </GridItem>
          </Grid>
          <FormConsumer>
            {
              ({
                form,
              }) => {
                formRef.current = form
              }
            }
          </FormConsumer>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          type="strong"
          onClick={handleSubmit}
        >
          <Locale id={okText} />
        </Button>
        <Button
          type="secondary"
          onClick={handleReject}
        >
          <Locale id={cancelText} />
        </Button>
      </ModalFooter>
    </Modal>

  )
}

VerifyPhoneStepTwo.propTypes = {
  handleConfirm: func.isRequired,
  handleResend: func.isRequired,
  handleReject: func.isRequired,
  handleClose: func.isRequired,
  i18n: func.isRequired,
  phoneNumber: string.isRequired,
  title: string.isRequired,
  okText: string.isRequired,
  countDownNumber: number,
  code: string,
}

VerifyPhoneStepTwo.defaultProps = {
  countDownNumber: '',
  code: '',
}
export default memo(withI18n(VerifyPhoneStepTwo))
